import { Colors } from '../colorUtils';
import { findCustomValue } from '../customValuesUtils';
import { calendarDateTimeFromTimestamp, getDateTimeDifference, type DateTime } from '../dateutils';
import { isIntegrationActive } from '../integrations';
import { isNilOrEmpty } from '../objectUtils';
import {
  InvoiceStatus,
  InvoiceType,
  type Contact,
  type CustomValue,
  type CustomValueDTO,
  type Integration,
  type Invoice,
} from '../types';
import type { InvoicePeppolStatus } from '../types/dto-admintool';

export enum InvoicePeppolCustomValueName {
  PEPPOL_STATUS_FOR_CUSTOM_VALUE = 'peppolStatus',
  PEPPOL_ERROR_TYPE_FOR_CUSTOM_VALUE = 'peppolErrorType',
  PEPPOL_ERROR_MESSAGE_FOR_CUSTOM_VALUE = 'peppolErrorMessage',
  PEPPOL_ID_FOR_CUSTOM_VALUE = 'peppolId',
  PEPPOL_RESEND_IN_FLIGHT_SINCE_FOR_CUSTOM_VALUE = 'peppolResendInFlightSince',
  PEPPOL_RESEND_IN_FLIGHT_MESSAGE_ID_FOR_CUSTOM_VALUE = 'peppolResendInFlightMessageId',
  PEPPOL_SEND_STARTED_AT_FOR_CUSTOM_VALUE = 'peppolSendStartedAt',
  PEPPOL_WAITING_FOR_COMMUNICATION_FOR_CUSTOM_VALUE = 'peppolWaitingForCommunication',
}

const PEPPOL_WAITING_INVOICE_IDS_SEPARATOR = ',';

/** Invoices sent on Peppol once this invoice's communication goes out: itself, and a VAT statement's credit note */
export const getPeppolInvoiceIdsWaitingForCommunication = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined
): string[] => {
  const waitingInvoiceIds = findCustomValue(
    customValues,
    InvoicePeppolCustomValueName.PEPPOL_WAITING_FOR_COMMUNICATION_FOR_CUSTOM_VALUE
  );
  return isNilOrEmpty(waitingInvoiceIds) ? [] : waitingInvoiceIds.split(PEPPOL_WAITING_INVOICE_IDS_SEPARATOR);
};

export const isPeppolWaitingForCommunication = (invoice: Pick<Invoice, 'id' | 'customValues'>): boolean =>
  getPeppolInvoiceIdsWaitingForCommunication(invoice.customValues).includes(invoice.id);

export const buildPeppolWaitingForCommunicationCustomValue = (invoiceIds: string[]): CustomValue => ({
  name: InvoicePeppolCustomValueName.PEPPOL_WAITING_FOR_COMMUNICATION_FOR_CUSTOM_VALUE,
  value: invoiceIds.join(PEPPOL_WAITING_INVOICE_IDS_SEPARATOR),
});

// A resend hands the invoice to the Peppol send queue and returns before that queue writes the
// final status, so the in-flight marker is what stops a second resend from submitting again.
// It is dropped as soon as a terminal status is written; the window only bounds the case where
// the send never completes at all, so that a stuck invoice stays resendable.
export const PEPPOL_RESEND_IN_FLIGHT_WINDOW_IN_MINUTES = 15;

export const isPeppolResendInFlight = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined,
  now: DateTime
): boolean => {
  const inFlightSince = findCustomValue(
    customValues,
    InvoicePeppolCustomValueName.PEPPOL_RESEND_IN_FLIGHT_SINCE_FOR_CUSTOM_VALUE
  );
  if (isNilOrEmpty(inFlightSince)) {
    return false;
  }
  const elapsedMinutes = getDateTimeDifference(
    { start: calendarDateTimeFromTimestamp(inFlightSince), end: now },
    'minutes'
  );
  return elapsedMinutes < PEPPOL_RESEND_IN_FLIGHT_WINDOW_IN_MINUTES;
};

// Below the Peppol queue's 900 s visibility timeout, so the only retry before the DLQ already finds a crashed claim
// abandoned, and above the send request timeout, so no attempt can still be running once its claim counts as abandoned.
export const PEPPOL_SEND_CLAIM_ABANDONED_AFTER_MINUTES = 10;

// Above the queue's 900 s visibility timeout: a reset stays refused until the one redelivery of a crashed send has
// recorded its outcome, so a document that may already be on Peppol never goes back to draft unnoticed.
export const PEPPOL_SEND_CLAIM_RESET_LOCK_IN_MINUTES = 20;

export const isPeppolSendClaimHeld = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined,
  now: DateTime
): boolean => {
  const sendStartedAt = findCustomValue(
    customValues,
    InvoicePeppolCustomValueName.PEPPOL_SEND_STARTED_AT_FOR_CUSTOM_VALUE
  );
  if (isNilOrEmpty(sendStartedAt)) {
    return false;
  }
  const elapsedMinutes = getDateTimeDifference(
    { start: calendarDateTimeFromTimestamp(sendStartedAt), end: now },
    'minutes'
  );
  return elapsedMinutes < PEPPOL_SEND_CLAIM_RESET_LOCK_IN_MINUTES;
};

// The queue message that claimed the marker gets redelivered under the same id when its handoff
// failed: that retry must go through, while any other resend still waits for the marker.
export const isPeppolResendInFlightForAnotherMessage = (
  customValues: CustomValue[] | CustomValueDTO[] | null | undefined,
  now: DateTime,
  messageId: string
): boolean =>
  isPeppolResendInFlight(customValues, now) &&
  findCustomValue(customValues, InvoicePeppolCustomValueName.PEPPOL_RESEND_IN_FLIGHT_MESSAGE_ID_FOR_CUSTOM_VALUE) !==
    messageId;

const PEPPOL_UNSENDABLE_INVOICE_STATUSES = new Set<string>([InvoiceStatus.DRAFT, InvoiceStatus.REVERSED]);
const PEPPOL_UNSENDABLE_INVOICE_TYPES = new Set<string>([
  InvoiceType.VARIOUS_OPERATION,
  InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
]);

export const isLeaseDocumentSendableOnPeppol = (invoice: Pick<Invoice, 'amount' | 'status' | 'type'>): boolean =>
  !PEPPOL_UNSENDABLE_INVOICE_STATUSES.has(invoice.status) &&
  !PEPPOL_UNSENDABLE_INVOICE_TYPES.has(invoice.type) &&
  invoice.amount !== 0;

export interface VerifyRegisteredCompanyResponse {
  contactId: string;
  isContactVerified: boolean;
}

export enum PeppolErrorType {
  INTEGRATION_NOT_ACTIVE = 'INTEGRATION_NOT_ACTIVE',
  NOT_ENOUGH_CREDITS = 'NOT_ENOUGH_CREDITS',
  MULTIPLE_RECIPIENTS = 'MULTIPLE_RECIPIENTS',
  PEPPOL_EDGE_INTERNAL_ERROR = 'PEPPOL_EDGE_INTERNAL_ERROR',
  PEPPOL_EDGE_VALIDATION_ERROR = 'PEPPOL_EDGE_VALIDATION_ERROR',
  SENDER_MUST_NOT_SEND = 'SENDER_MUST_NOT_SEND',
  SENDER_NOT_COMPANY = 'SENDER_NOT_COMPANY',
  SENDER_NOT_REGISTERED = 'SENDER_NOT_REGISTERED',
  SENDER_WITHOUT_POSTAL_ADDRESS = 'SENDER_WITHOUT_POSTAL_ADDRESS',
  RECIPIENT_NOT_COMPANY = 'RECIPIENT_NOT_COMPANY',
  RECIPIENT_NOT_REGISTERED = 'RECIPIENT_NOT_REGISTERED',
  RECIPIENT_WITHOUT_POSTAL_ADDRESS = 'RECIPIENT_WITHOUT_POSTAL_ADDRESS',
  NOT_VAT_LIABLE = 'NOT_VAT_LIABLE',
  SEND_OUTCOME_UNKNOWN = 'SEND_OUTCOME_UNKNOWN',
  COMMUNICATION_DELETED = 'COMMUNICATION_DELETED',
}

// Must be a method to avoid undefined `Colors` during circular module initialization.
export const getPeppolStatusColorMap = (): Record<InvoicePeppolStatus, Colors> =>
  ({
    FAILED: Colors.CARNATION_RED,
    ERROR: Colors.CARNATION_RED,
    SENT: Colors.DODGER_BLUE,
    PENDING: Colors.DODGER_BLUE,
    COMPLETED: Colors.TURQUOISE_BLUE,
  }) as Record<InvoicePeppolStatus, Colors>;

export const getPeppolStatusColor = (status: InvoicePeppolStatus | null | undefined): Colors =>
  getPeppolStatusColorMap()[status as InvoicePeppolStatus] ?? Colors.CARNATION_RED;

export const getPeppolError = (
  recipients: Contact[],
  invoiceSender?: Contact | null,
  peppolIntegration?: Integration,
  documentsAreVatLiable = true
): PeppolErrorType | null => {
  if (!isIntegrationActive(peppolIntegration)) {
    return PeppolErrorType.INTEGRATION_NOT_ACTIVE;
  }
  if (!documentsAreVatLiable) {
    return PeppolErrorType.NOT_VAT_LIABLE;
  }
  if (!invoiceSender || isNilOrEmpty(invoiceSender.businessNumber)) {
    return PeppolErrorType.SENDER_NOT_COMPANY;
  }
  if (!invoiceSender.peppolProperties?.mustSend) {
    return PeppolErrorType.SENDER_MUST_NOT_SEND;
  }
  if (!invoiceSender.peppolProperties.isRegistered) {
    return PeppolErrorType.SENDER_NOT_REGISTERED;
  }
  if (!invoiceSender.addressId) {
    return PeppolErrorType.SENDER_WITHOUT_POSTAL_ADDRESS;
  }
  if (recipients.length > 1) {
    return PeppolErrorType.MULTIPLE_RECIPIENTS;
  }
  const tenant = recipients[0];
  if (isNilOrEmpty(tenant.businessNumber)) {
    return PeppolErrorType.RECIPIENT_NOT_COMPANY;
  }
  if (!tenant.peppolProperties?.isRegistered) {
    return PeppolErrorType.RECIPIENT_NOT_REGISTERED;
  }
  if (!tenant.addressId) {
    return PeppolErrorType.RECIPIENT_WITHOUT_POSTAL_ADDRESS;
  }
  return null;
};

// BT-14 (cac:OrderReference/cbc:SalesOrderID) is the seller's own order reference. When the tenant
// gave us no purchase order, the lease is the only stable handle the recipient can match the
// invoice against, so it is sent under that element rather than faked into BT-13.
// The only two errors the Peppol directory itself can clear.
export const VERIFIABLE_PEPPOL_ERRORS: PeppolErrorType[] = [
  PeppolErrorType.SENDER_NOT_REGISTERED,
  PeppolErrorType.RECIPIENT_NOT_REGISTERED,
];

export const isPeppolRegistrationUnverified = (contact: Contact): boolean =>
  !isNilOrEmpty(contact.businessNumber) && contact.peppolProperties?.isRegistered !== true;

export const withVerifiedRegistration = (contact: Contact, verifiedContactIds: string[]): Contact =>
  verifiedContactIds.includes(contact.id)
    ? {
        ...contact,
        peppolProperties: { mustSend: contact.peppolProperties?.mustSend ?? false, isRegistered: true },
      }
    : contact;

export const getLeaseSalesOrderId = (leaseId: string): string => `LEASE-${leaseId}`;
